import instance, { ApiResponse } from 'api';
import { Modal } from 'components/modal/Modal';
import {
    completeUIUpdate,
    NotificationIdentifier,
    selectUIUpdate,
} from 'components/notifications/notificationUIUpdateReducer';
import { endpoints } from 'endpoints.config';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from 'reducers/auth';
import { isEqual } from 'lodash';
import { JumioIframe } from './JumioIdIframe';
import { JumioTextualData } from './JumioTextualData';
import { JumioVerificationComplete } from './JumioVerificationComplete';
import { ManualVerificationRequired } from './JumioManualVerificationRequired';
import Button from 'components/button/Button';
import {
    fetchVerificationStatus,
    selectVerificationDetails,
    selectVerificationLoading,
    setVerificationStage,
} from 'reducers/verification';
import { JumioStage, JumioVerificationStatusResponse } from './jumioModels';
import { Spinner } from 'components/spinner/Spinner';
import { JumioFailure } from './JumioFailure';
import { JumioCooldown } from './JumioCooldown';
import { JumioRejected } from './JumioRejected';
import { Toast } from 'helpers/toast';
import { JumioConsent } from './JumioConsent';
import { PersonalAcknowledgementsBeforeJumio } from './PersonalAcknowledgementsBeforeJumio';
import { PendingAdminApproval } from './PendingAdminApproval';
import { selectComponentResources } from 'reducers/componentResources';
import { useBrandIdentity } from 'hooks/useBrandIdentity';

export type VerifyIdentityOptions = {
    countryOptions: { isoCode: string; name: string }[];
    documentOptions: { typeCode: string; name: string }[];
};

const DDA_PRODUCT_ID = 1006;

type TermsFlags = {
    bTaxAcknowledgement?: boolean;
    bFatcaAcknowledgement?: boolean;
    bConfirmTrueAndCorrect?: boolean;
    bAcceptTerms?: boolean;
    bServiceFeeAcknowledgement?: boolean;
};

export const JumioVerification = () => {
    const dispatch = useDispatch();
    const update = useSelector(selectUIUpdate);
    const { userInfo } = useSelector(selectComponentResources);
    const [verifyIdentityOptions, setVerifyIdentityOptions] =
        useState<VerifyIdentityOptions | null>(null);
    const [personalAcknowledgementsComplete, setPersonalAcknowledgementsComplete] = useState(false);
    const { stage, additionalData } = useSelector(selectVerificationDetails);
    const loading = useSelector(selectVerificationLoading);
    const { brandKey } = useBrandIdentity();
    // The Service Fee acknowledgement is specific to the 75 Pillars (jhglobal) brand.
    const isJhGlobal = brandKey === 'jhglobal';

    const hasDDAProduct = useMemo(() => {
        if (!userInfo?.productServices || userInfo?.accountType !== 'Personal') return false;
        const productIds = userInfo.productServices
            .split(',')
            .map((id: string) => parseInt(id.trim(), 10))
            .filter((id) => !Number.isNaN(id));

        return productIds.length === 1 && productIds[0] === DDA_PRODUCT_ID;
    }, [userInfo?.productServices, userInfo?.accountType]);

    // QA Finding #6: skip the "Terms and Acknowledgements" screen if the user already accepted it
    // previously (flags persisted server-side), so it isn't re-requested on every reload.
    useEffect(() => {
        let cancelled = false;
        instance
            .get<ApiResponse<TermsFlags>>(endpoints.personalVerification.getCustomerUserTerms)
            .then((res) => {
                const t = res.data.details;
                const alreadyAccepted =
                    !!t?.bTaxAcknowledgement &&
                    !!t?.bConfirmTrueAndCorrect &&
                    !!t?.bAcceptTerms &&
                    // FATCA is only collected for non-DDA products.
                    (hasDDAProduct || !!t?.bFatcaAcknowledgement) &&
                    // Service Fee acknowledgement is only collected for the 75 Pillars (jhglobal) brand.
                    (!isJhGlobal || !!t?.bServiceFeeAcknowledgement);
                if (!cancelled && alreadyAccepted) setPersonalAcknowledgementsComplete(true);
            })
            .catch(() => {
                /* leave the screen showing on error */
            });
        return () => {
            cancelled = true;
        };
    }, [hasDDAProduct, isJhGlobal]);

    useEffect(() => {
        if (update?.pushType !== NotificationIdentifier.UPDATE_VERIFICATION_COMPONENTS) return;
        dispatch(
            setVerificationStage({
                currentStage: (update.data as JumioVerificationStatusResponse).status,
                additionalData: update.data.additionalData,
            })
        );
    }, [update, dispatch]);

    useEffect(() => {
        if (
            (stage === JumioStage.AddressFrameForm || stage === JumioStage.IdFrameSubmitted) &&
            !verifyIdentityOptions
        ) {
            instance
                .get<ApiResponse<VerifyIdentityOptions>>(endpoints.jumiomodule.verifyIdentity)
                .then((res) => {
                    setVerifyIdentityOptions(res.data.details);
                })
                .catch((err) => Toast.openGenericErrorToast());
        }
    }, [stage, verifyIdentityOptions]);

    useEffect(() => {
        // Only poll when in a pending/submitted state
        if (
            stage === JumioStage.IdFrameSubmitted ||
            stage === JumioStage.AddressFrameSubmitted ||
            stage === JumioStage.PendingVerification
        ) {
            const timeout = setTimeout(() => dispatch(fetchVerificationStatus()), 20000);
            return () => clearTimeout(timeout);
        }
    }, [stage, dispatch]);

    if (loading) {
        return null;
    }

    // Show personal acknowledgements FIRST if user has DDA product and hasn't completed them yet
    // This must appear before any Jumio verification stage
    if (!personalAcknowledgementsComplete) {
        return (
            <Modal
                title="Customer verification"
                customCloseButton={
                    <span className="SignOutLink" onClick={() => dispatch(signOut())}>
                        Sign out
                    </span>
                }
                className="JumioModal"
                backgroundClassName="JumioModalBg"
            >
                <PersonalAcknowledgementsBeforeJumio
                    hasDDAProduct={hasDDAProduct}
                    onComplete={() => {
                        setPersonalAcknowledgementsComplete(true);
                        // Fetch verification status to proceed to next Jumio stage
                        dispatch(fetchVerificationStatus());
                    }}
                />
            </Modal>
        );
    }

    const chooseStage = () => {
        switch (stage) {
            case JumioStage.IdFrame:
                return (
                    additionalData?.iframeUrl && (
                        <JumioIframe url={additionalData?.iframeUrl} stage={stage} />
                    )
                );
            case JumioStage.IdFrameSubmitted:
                return <PendingScreen />;
            case JumioStage.IdFrameFailed:
                return <JumioFailure bManualAvailable={!!additionalData?.bManualAvailable} />;
            case JumioStage.AddressFrameForm:
                return (
                    verifyIdentityOptions && <JumioTextualData options={verifyIdentityOptions} />
                );
            case JumioStage.AddressFrame:
                return (
                    additionalData?.iframeUrl && (
                        <JumioIframe url={additionalData?.iframeUrl} stage={stage} />
                    )
                );
            case JumioStage.AddressFrameSubmitted:
                return <PendingScreen />;
            case JumioStage.AddressFrameFailed:
                return <JumioFailure bManualAvailable={!!additionalData?.bManualAvailable} />;
            case JumioStage.AwaitingManualVerification:
                return <ManualVerificationRequired />;
            case JumioStage.PendingVerification:
                return <PendingAdminApproval />;
            case JumioStage.FrameCooldown:
                return <JumioCooldown bManualAvailable={!!additionalData?.bManualAvailable} />;
            case JumioStage.Success:
                return <JumioVerificationComplete />;
            case JumioStage.Rejected:
                return <JumioRejected />;
        }
    };

    return (
        <Modal
            title="Customer verification"
            customCloseButton={
                <span className="SignOutLink" onClick={() => dispatch(signOut())}>
                    Sign out
                </span>
            }
            className="JumioModal"
            backgroundClassName="JumioModalBg"
        >
            {chooseStage()}
        </Modal>
    );
};

const PendingScreen = () => {
    return (
        <div className="Waiting">
            <Spinner />
            <h3>Please wait, we are checking your details</h3>
            <p>This may take a few minutes, we'll email you when it's done.</p>
        </div>
    );
};
